import { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import Select from 'react-select';
import { DateRangePicker } from 'rsuite';
import { FileText, Pencil, Copy, FilePlus, MessagesSquare, Trash2 } from 'lucide-react';
import { cotizacionesAPI, tiposServicioAPI, alertasAPI } from '../services/api';
import { moneyFmt, formatDate, getEstadoColor } from '../utils/helpers';
import { generatePdfCotizacion } from '../utils/pdfGenerator';
import { useAuth } from '../context/AuthContext';

// Estilos personalizados para react-select (tema oscuro)
const selectStyles = {
    control: (base, state) => ({
        ...base,
        backgroundColor: 'rgba(20, 30, 60, 0.7)',
        borderColor: state.isFocused ? 'rgba(140, 220, 255, 0.5)' : 'rgba(140, 220, 255, 0.15)',
        borderRadius: '8px',
        minHeight: '38px',
        boxShadow: state.isFocused ? '0 0 0 1px rgba(140, 220, 255, 0.3)' : 'none',
        '&:hover': {
            borderColor: 'rgba(140, 220, 255, 0.3)'
        }
    }),
    menu: (base) => ({
        ...base,
        backgroundColor: 'rgba(20, 30, 60, 0.98)',
        border: '1px solid rgba(140, 220, 255, 0.2)',
        borderRadius: '8px',
        zIndex: 100
    }),
    menuList: (base) => ({
        ...base,
        padding: '4px 0',
        '::-webkit-scrollbar': {
            width: '6px'
        },
        '::-webkit-scrollbar-track': {
            background: 'rgba(255, 255, 255, 0.05)',
            borderRadius: '3px'
        },
        '::-webkit-scrollbar-thumb': {
            background: 'rgba(140, 220, 255, 0.3)',
            borderRadius: '3px'
        },
        '::-webkit-scrollbar-thumb:hover': {
            background: 'rgba(140, 220, 255, 0.5)'
        }
    }),
    option: (base, state) => ({
        ...base,
        backgroundColor: state.isSelected
            ? 'rgba(140, 220, 255, 0.2)'
            : state.isFocused
                ? 'rgba(140, 220, 255, 0.1)'
                : 'transparent',
        color: state.isSelected ? '#8cdcff' : '#e0e6ed',
        cursor: 'pointer',
        fontSize: '13px',
        padding: '8px 12px',
        '&:active': {
            backgroundColor: 'rgba(140, 220, 255, 0.2)'
        }
    }),
    singleValue: (base) => ({
        ...base,
        color: '#e0e6ed',
        fontSize: '14px'
    }),
    input: (base) => ({
        ...base,
        color: '#e0e6ed',
        fontSize: '14px'
    }),
    placeholder: (base) => ({
        ...base,
        color: 'rgba(224, 230, 237, 0.5)',
        fontSize: '14px'
    }),
    indicatorSeparator: () => ({
        display: 'none'
    }),
    dropdownIndicator: (base) => ({
        ...base,
        color: 'rgba(140, 220, 255, 0.5)',
        padding: '6px',
        '&:hover': {
            color: 'rgba(140, 220, 255, 0.8)'
        }
    }),
    menuPortal: (base) => ({
        ...base,
        zIndex: 9999
    })
};

// Opciones de estado
const estadoOptions = [
    { value: '', label: 'Todos' },
    { value: 'borrador', label: 'Borrador' },
    { value: 'enviada', label: 'Enviada' },
    { value: 'aprobada', label: 'Aprobada' },
    { value: 'rechazada', label: 'Rechazada' }
];

const estadoOptionsNoTodos = [
    { value: 'borrador', label: 'Borrador' },
    { value: 'enviada', label: 'Enviada' },
    { value: 'aprobada', label: 'Aprobada' },
    { value: 'rechazada', label: 'Rechazada' }
];

const CotizacionesList = ({ onEdit, onNueva, configuracion }) => {
    const { tienePermiso } = useAuth();
    const verPdf = tienePermiso('ver_pdf');
    const [cotizaciones, setCotizaciones] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [dateRange, setDateRange] = useState(null);
    const [filtros, setFiltros] = useState({
        estado: '',
        cliente: '',
        desde: '',
        hasta: ''
    });
    const [actividadModal, setActividadModal] = useState(null);
    const [historial, setHistorial] = useState([]);
    const [loadingHistorial, setLoadingHistorial] = useState(false);

    const abrirHistorial = async (cot) => {
        setActividadModal(cot);
        setLoadingHistorial(true);
        try {
            const res = await alertasAPI.getActividades(cot.id);
            setHistorial(res.data || []);
        } catch (err) {
            setHistorial([]);
        } finally {
            setLoadingHistorial(false);
        }
    };

    const cerrarHistorial = () => {
        setActividadModal(null);
        setHistorial([]);
    };

    const handleDateRangeChange = (value) => {
        setDateRange(value);
        if (value && value[0] && value[1]) {
            setFiltros(f => ({
                ...f,
                desde: value[0].toISOString().split('T')[0],
                hasta: value[1].toISOString().split('T')[0]
            }));
        } else {
            setFiltros(f => ({ ...f, desde: '', hasta: '' }));
        }
    };

    const cargarCotizaciones = async () => {
        setLoading(true);
        try {
            const params = {};
            if (filtros.estado) params.estado = filtros.estado;
            if (filtros.cliente) params.cliente = filtros.cliente;
            if (filtros.desde) params.desde = filtros.desde;
            if (filtros.hasta) params.hasta = filtros.hasta;

            const response = await cotizacionesAPI.getAll(params);
            setCotizaciones(response.data || []);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        cargarCotizaciones();
    }, []);

    const handleFiltrar = (e) => {
        e.preventDefault();
        cargarCotizaciones();
    };

    const handleEliminar = (id, numero) => {
        toast((t) => (
            <div className="toast-confirm">
                <p>¿Eliminar la cotización <strong>{numero}</strong>?</p>
                <div className="toast-confirm-buttons">
                    <button
                        className="toast-btn toast-btn-cancel"
                        onClick={() => toast.dismiss(t.id)}
                    >
                        Cancelar
                    </button>
                    <button
                        className="toast-btn toast-btn-danger"
                        onClick={async () => {
                            toast.dismiss(t.id);
                            try {
                                await cotizacionesAPI.delete(id);
                                toast.success(`Cotización ${numero} eliminada`);
                                cargarCotizaciones();
                            } catch (err) {
                                toast.error('Error al eliminar: ' + err.message);
                            }
                        }}
                    >
                        Eliminar
                    </button>
                </div>
            </div>
        ), {
            duration: Infinity,
            style: {
                maxWidth: '400px',
            }
        });
    };

    const handleDuplicar = async (id) => {
        try {
            const response = await cotizacionesAPI.duplicate(id);
            toast.success(`Cotización duplicada: ${response.data.numero}`);
            cargarCotizaciones();
        } catch (err) {
            toast.error('Error al duplicar: ' + err.message);
        }
    };

    const handleNuevaVersion = async (id) => {
        try {
            const response = await cotizacionesAPI.createVersion(id);
            toast.success(`Nueva versión creada: ${response.data.numero}`);
            cargarCotizaciones();
        } catch (err) {
            toast.error('Error al crear versión: ' + err.message);
        }
    };

    const handleCambiarEstado = async (id, nuevoEstado) => {
        try {
            await cotizacionesAPI.updateEstado(id, nuevoEstado);
            cargarCotizaciones();
        } catch (err) {
            toast.error('Error al cambiar estado: ' + err.message);
        }
    };

    const handleVerPdf = async (id, preview = true) => {
        try {
            const response = await cotizacionesAPI.getById(id);
            const { servicios: serviciosData, extras, ...cotizacionBase } = response.data;
            const tiposRes = await tiposServicioAPI.getAll();

            const igvRate = parseFloat(configuracion?.igv_rate) || 0.18;
            const rentabilidad = parseFloat(configuracion?.rentabilidad) || 0.68;

            // Recalcular servicios con costo y total
            const servicios = (serviciosData || []).map(s => {
                const asignaciones = (s.asignaciones || []).map(a => {
                    const horas = parseFloat(a.horas) || 0;
                    const costoHora = parseFloat(a.costo_hora) || 0;
                    const horasAdicionales = horas * 0.12;
                    return {
                        ...a,
                        horas_adicionales: horasAdicionales,
                        subtotal: (horas + horasAdicionales) * costoHora
                    };
                });

                const costoServicio = asignaciones.reduce((acc, a) => acc + (parseFloat(a.subtotal) || 0), 0);

                return {
                    ...s,
                    asignaciones,
                    costo: costoServicio,
                    total: costoServicio / (1 - rentabilidad)
                };
            });

            // Calcular totales
            const subtotalServicios = servicios.reduce((acc, s) => acc + (parseFloat(s.total) || 0), 0);
            const subtotalExtras = (extras || []).reduce((acc, e) => acc + (parseFloat(e.costo) || 0), 0);
            const subtotal = subtotalServicios;
            const igv = subtotal * igvRate;
            const total = subtotal + igv + subtotalExtras;

            const cotizacion = {
                ...cotizacionBase,
                subtotal,
                subtotalExtras,
                igv,
                total
            };

            await generatePdfCotizacion({
                cotizacion,
                servicios,
                extras,
                configuracion,
                tiposServicio: tiposRes.data || [],
                preview
            });
        } catch (err) {
            toast.error('Error al generar PDF: ' + err.message);
        }
    };

    if (loading) {
        return <div className="loading">Cargando cotizaciones...</div>;
    }

    if (error) {
        return <div className="error">Error: {error}</div>;
    }

    return (
    <>
        <section className="card">
            <div className="cardHead">
                <h2 className="h2">Historial de Cotizaciones</h2>
                <div className="cardHead-actions">
                    <button className="btn ghost" onClick={cargarCotizaciones}>
                        Actualizar listado
                    </button>
                    <button className="btn neon" onClick={onNueva}>
                        + Nueva Cotización
                    </button>
                </div>
            </div>

            {/* Filtros */}
            <form className="filtros-form" onSubmit={handleFiltrar}>
                <div className="filtros-grid">
                    <label className="field">
                        <span className="label">Cliente</span>
                        <input
                            className="input"
                            type="text"
                            value={filtros.cliente}
                            onChange={(e) => setFiltros(f => ({ ...f, cliente: e.target.value }))}
                            placeholder="Buscar por cliente..."
                        />
                    </label>

                    <div className="field">
                        <span className="label">Estado</span>
                        <Select
                            styles={selectStyles}
                            options={estadoOptions}
                            value={estadoOptions.find(opt => opt.value === filtros.estado) || estadoOptions[0]}
                            onChange={(selected) => setFiltros(f => ({ ...f, estado: selected?.value || '' }))}
                            placeholder="Todos"
                            isSearchable={false}
                            noOptionsMessage={() => 'Sin opciones'}
                        />
                    </div>

                    <div className="field field-daterange">
                        <span className="label">Rango de fechas</span>
                        <DateRangePicker
                            value={dateRange}
                            onChange={handleDateRangeChange}
                            format="dd/MM/yyyy"
                            character=" - "
                            placeholder="Seleccionar rango"
                            showOneCalendar
                            placement="bottomEnd"
                            className="rs-theme-dark"
                            ranges={[]}
                        />
                    </div>

                    <button className="btn ghost" type="submit">
                        Filtrar
                    </button>
                </div>
            </form>

            {/* Tabla de cotizaciones */}
            <div className="tableWrap">
                <table className="galTable">
                    <thead>
                        <tr>
                            <th>Número</th>
                            <th>Cliente</th>
                            <th>Fecha</th>
                            <th>Total</th>
                            <th>Estado</th>
                            <th style={{ textAlign: 'center', width: '80px' }}>Actividad</th>
                            <th>Acciones</th>
                        </tr>
                    </thead>
                    <tbody>
                        {cotizaciones.length === 0 ? (
                            <tr>
                                <td colSpan="7" className="empty-message">
                                    No hay cotizaciones registradas
                                </td>
                            </tr>
                        ) : (
                            cotizaciones.map((cot) => (
                                <tr key={cot.id}>
                                    <td className="strong">
                                        <button
                                            className="link-btn"
                                            onClick={() => onEdit(cot.id)}
                                            title="Ver cotización"
                                        >
                                            {cot.numero}
                                        </button>
                                    </td>
                                    <td>
                                        <div>{cot.cliente_nombre}</div>
                                        {cot.cliente_empresa && (
                                            <small className="text-muted">{cot.cliente_empresa}</small>
                                        )}
                                    </td>
                                    <td>{formatDate(cot.fecha)}</td>
                                    <td className="right strong">
                                        {moneyFmt(cot.total, cot.moneda)}
                                    </td>
                                    <td className="estado-cell">
                                        <Select
                                            styles={{
                                                ...selectStyles,
                                                control: (base, state) => ({
                                                    ...selectStyles.control(base, state),
                                                    borderColor: state.isFocused
                                                        ? 'rgba(140, 220, 255, 0.5)'
                                                        : getEstadoColor(cot.estado),
                                                    minHeight: '32px'
                                                }),
                                                dropdownIndicator: (base) => ({
                                                    ...base,
                                                    color: getEstadoColor(cot.estado),
                                                    padding: '4px',
                                                    '&:hover': {
                                                        color: 'rgba(140, 220, 255, 0.8)'
                                                    }
                                                })
                                            }}
                                            options={estadoOptionsNoTodos}
                                            value={estadoOptionsNoTodos.find(opt => opt.value === cot.estado)}
                                            onChange={(selected) => handleCambiarEstado(cot.id, selected?.value)}
                                            isSearchable={false}
                                            menuPortalTarget={document.body}
                                            menuPosition="fixed"
                                        />
                                    </td>
                                    <td style={{ textAlign: 'center' }}>
                                        <button
                                            className="btn-icon seguimiento"
                                            onClick={() => abrirHistorial(cot)}
                                            title="Seguimiento"
                                        >
                                            <MessagesSquare size={16} />
                                            {cot.total_actividades > 0 && (
                                                <span className="actividad-count-badge">{cot.total_actividades}</span>
                                            )}
                                        </button>
                                    </td>
                                    <td>
                                        <div className="acciones-btns">
                                            {verPdf && (
                                                <button
                                                    className="btn-icon pdf"
                                                    onClick={() => handleVerPdf(cot.id, cot.estado === 'borrador')}
                                                    title={cot.estado === 'borrador' ? 'Previsualizar PDF' : 'Descargar PDF'}
                                                >
                                                    <FileText size={16} />
                                                </button>
                                            )}
                                            <button
                                                className="btn-icon edit"
                                                onClick={() => onEdit(cot.id)}
                                                title="Editar"
                                            >
                                                <Pencil size={16} />
                                            </button>
                                            <button
                                                className="btn-icon duplicate"
                                                onClick={() => handleDuplicar(cot.id)}
                                                title="Duplicar"
                                            >
                                                <Copy size={16} />
                                            </button>
                                            <button
                                                className="btn-icon version"
                                                onClick={() => handleNuevaVersion(cot.id)}
                                                title="Nueva Versión"
                                            >
                                                <FilePlus size={16} />
                                            </button>
                                            <button
                                                className="btn-icon delete"
                                                onClick={() => handleEliminar(cot.id, cot.numero)}
                                                title="Eliminar"
                                            >
                                                <Trash2 size={16} />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            <div className="table-footer">
                <span className="total-count">
                    Total: {cotizaciones.length} cotización(es)
                </span>
            </div>

        </section>

            {/* Modal de historial de seguimiento */}
            {actividadModal && (
                <div className="modal-overlay" onClick={cerrarHistorial}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '520px' }}>
                        <div className="modal-header">
                            <div>
                                <h3 style={{ margin: 0 }}>Seguimiento</h3>
                                <span style={{ color: 'var(--muted)', fontSize: '13px' }}>
                                    {actividadModal.numero} - {actividadModal.cliente_nombre}
                                </span>
                            </div>
                            <button className="modal-close" onClick={cerrarHistorial}>&times;</button>
                        </div>
                        <div className="modal-body">
                            <h4 style={{ color: 'var(--text)', marginBottom: '8px', marginTop: 0 }}>Historial de actividades</h4>
                            {loadingHistorial ? (
                                <p style={{ color: 'var(--muted)', fontSize: '13px' }}>Cargando...</p>
                            ) : historial.length === 0 ? (
                                <p style={{ color: 'var(--muted)', fontSize: '13px' }}>Sin actividades registradas</p>
                            ) : (
                                <div className="actividad-historial">
                                    {historial.map(act => (
                                        <div key={act.id} className="actividad-item">
                                            <div className="actividad-item-dot" />
                                            <div className="actividad-item-content">
                                                <div className="actividad-item-tipo">{act.tipo_nombre}</div>
                                                {act.descripcion && (
                                                    <div className="actividad-item-desc">{act.descripcion}</div>
                                                )}
                                                <div className="actividad-item-meta">
                                                    {act.usuario_nombre} - {new Date(act.created_at).toLocaleDateString('es-PE')} {new Date(act.created_at).toLocaleTimeString('es-PE', { hour: '2-digit', minute: '2-digit' })}
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
    </>
    );
};

export default CotizacionesList;
